import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBaby, faStethoscope, faUserShield, faShieldAlt, faMicroscope, faHandHoldingMedical, faHeartbeat, faUserMd, faHospital, faNotesMedical, faClipboardCheck, faUsers, faShoePrints, faLeaf } from '@fortawesome/free-solid-svg-icons';
import './Services.css';

const Services = () => {
  const [animatedRows, setAnimatedRows] = useState(new Set());
  const servicesRef = useRef(null);
  const appointmentRef = useRef(null);

  useEffect(() => {
    const animatedRowsRef = new Set();

    const checkRowVisibility = () => {
      const rows = document.querySelectorAll('.single-service-row');
      
      rows.forEach((row, index) => {
        const rowId = `service-row-${index}`;
        
        if (!animatedRowsRef.has(rowId)) {
          const rect = row.getBoundingClientRect();
          const windowHeight = window.innerHeight;
          
          // Special case: First row should animate immediately if it's visible on page load
          const shouldAnimate = index === 0 
            ? rect.top < windowHeight && rect.bottom > 0
            : rect.top < windowHeight * 0.7 && rect.bottom > 0;
          
          if (shouldAnimate) {
            animatedRowsRef.add(rowId);
            setAnimatedRows(prev => new Set([...prev, rowId]));
            
            // Animate this specific single service card
            const serviceCard = row.querySelector('.service-card');
            if (serviceCard) {
              serviceCard.style.opacity = '1';
              serviceCard.style.transform = 'translateX(0)';
              serviceCard.classList.add('animate-right');
            }
          }
        }
      });
    };

    // Initial check
    setTimeout(checkRowVisibility, 100);

    // Add scroll event listener
    const handleScroll = () => {
      checkRowVisibility();
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', checkRowVisibility);

    // Appointment section animation
    const checkAppointmentVisibility = () => {
      if (appointmentRef.current) {
        const rect = appointmentRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        if (rect.top < windowHeight * 0.8 && rect.bottom > 0) {
          const children = appointmentRef.current.querySelectorAll('.animate-on-scroll, .animate-right');
          children.forEach((child, index) => {
            setTimeout(() => {
              child.classList.add('animate');
            }, index * 100);
          });
        }
      }
    };

    const handleAppointmentScroll = () => {
      checkAppointmentVisibility();
    };

    window.addEventListener('scroll', handleAppointmentScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleAppointmentScroll);
      window.removeEventListener('resize', checkRowVisibility);
    };
  }, []);

  // Services data
  const services = [
    {
      id: 'skin-cancer-check',
      title: "Skin Cancer Check",
      description: "Comprehensive skin examinations and early detection services to identify and treat skin cancers effectively.",
      icon: faShieldAlt,
      image: '/images/Skin-cancer-screening-banner-scaled.webp'
    },
    /* ALTERNATIVE MEDICINE - commented out, uncomment to re-enable
    {
      id: 'alternative-medicine',
      title: "Alternative Medicine",
      description: "Complementary and alternative medicine approaches including naturopathy, herbal medicine, and holistic treatment options.",
      icon: faLeaf,
      image: '/images/alternative-medicine.jpg'
    },
    */
    {
      id: 'disease-management-program',
      title: 'Chronic Disease Management Program',
      description: 'Comprehensive management of chronic conditions including diabetes, hypertension, and heart disease.',
      icon: faShieldAlt,
      image: '/images/chronic-disease.png'
    },
    {
      id: 'employee-medical-assessments',
      title: "Employee Medical Assessments",
      description: "Occupational health services including pre-employment medicals, injury assessments, and workplace health programs.",
      icon: faHospital,
      image: '/images/employee assessment.webp'
    },
    {
      id: 'kids-health',
      title: "Kid's Health",
      description: "Comprehensive general practice care for infants, children, and adolescents, focusing on their physical, mental, and emotional well-being.",
      icon: faBaby,
      image: '/images/kids health.jpg'
    },
    {
      id: 'chronic-pain-therapy',
      title: "Chronic Pain Therapy",
      description: "Specialized treatment approaches for managing chronic pain conditions and improving quality of life.",
      icon: faHandHoldingMedical,
      image: '/images/chronicpaintherapy.webp'
    },
    {
      id: 'flu-prevention-care',
      title: "Flu Prevention Care",
      description: "Annual influenza vaccinations and immunization services to protect you and your family from seasonal flu.",
      icon: faShieldAlt,
      image: '/images/fluvaccine.jpg'
    },
    {
      id: 'womens-medical-care',
      title: "Women's Medical Care",
      description: "Comprehensive women's health services including reproductive health, pregnancy care, and specialized treatments.",
      icon: faHeartbeat,
      image: '/images/Women\'s Health & Shared Obstetric Care.jpg'
    },
    {
      id: 'travel-medical-care',
      title: "Travel Medical Care",
      description: "Pre-travel consultations, vaccinations, and health advice to keep you safe during international travel.",
      icon: faHeartbeat,
      image: '/images/Travel-vaccine-936x527.jpg'
    },
    {
      id: 'elderly-care-support',
      title: "Elderly Care Support",
      description: "Specialized geriatric care focusing on the complex health needs of older adults and maintaining independence.",
      icon: faHandHoldingMedical,
      image: '/images/Aged-Care-GP-Doctor-with-Resident.webp'
    },
    {
      id: 'on-site-pathology',
      title: "On-site pathology",
      description: "On-site pathology collection services with state-of-the-art technology for accurate and timely test results.",
      icon: faMicroscope,
      image: '/images/path-content.png'
    },
    {
      id: 'comprehensive-health-checks',
      title: "Comprehensive Health Checks",
      description: "Thorough health evaluations and preventive care assessments to maintain optimal health and early detection of potential issues.",
      icon: faClipboardCheck,
      image: '/images/Comprehensive Health Checks.jpg'
    },
    {
      id: 'occupational-injury-assessments',
      title: "Occupational Injury Assessments",
      description: "Professional assessment and treatment of workplace injuries with comprehensive rehabilitation support.",
      icon: faUserMd,
      image: '/images/workers-compensation.webp'
    },
    {
      id: 'teen-medical-support',
      title: "Teen Medical Support",
      description: "Specialized general practice services tailored to the unique physical and emotional needs of teenagers.",
      icon: faHeartbeat,
      image: '/images/adolescent-health_1.jpg'
    },
    {
      id: 'primary-care-services',
      title: "Primary Care Services",
      description: "Comprehensive primary general practice services for patients of all ages, including preventive care and treatment of acute and chronic conditions.",
      icon: faUserMd,
      image: '/images/medicin.jpg'
    },
    {
      id: 'mens-wellness-care',
      title: "Men's Wellness Care",
      description: "Specialized care addressing unique men's health concerns including prostate health, cardiovascular disease, and mental health.",
      icon: faUserShield,
      image: '/images/mens-health.jpg'
    },
    {
      id: 'aboriginal-torres-strait-islander-health',
      title: "Aboriginal and Torres Strait Islander Health",
      description: "Culturally safe and appropriate general practitioners services designed specifically for Aboriginal and Torres Strait Islander communities with respect for cultural practices and beliefs.",
      icon: faUsers,
      image: '/images/aboriginal.webp'
    },
    {
      id: 'foot-care',
      title: "Foot Care",
      description: "Comprehensive foot and ankle care including treatment of foot conditions, diabetic foot care, and biomechanical assessments.",
      icon: faShoePrints,
      image: '/images/foot care.jpg'
    }
  ];

  return (
    <div className="services-page">
      <Helmet>
        <title>Medical Services | Tea Gardens GP | Healthcare Services NSW</title>
        <meta name="description" content="Comprehensive medical services at Tea Gardens GP including skin cancer checks, chronic disease management, children's health, and more. Quality healthcare in NSW." />
        <link rel="canonical" href="https://teagardensgp.com.au/services" />
      </Helmet>
      {/* Page Header */}
      <div className="page-header">
        <div className="container">
          <h1>Services</h1>
          <p>Provide The Best Medical Service for You!</p>
        </div>
      </div>

      {/* Services List */}
      <section className="section services-list" ref={servicesRef} id="services">
        <div className="container">
          <div className="services-grid">
{services.map((service, index) => (
              <div 
                key={`service-row-${index}`}
                id={`service-row-${index}`}
                className="services-row single-service-row"
              >
                <div className="service-card" key={service.id} style={{opacity: 0, transform: 'translateX(100px)', transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)'}}>
                  <div className="service-image">
                    <img src={service.image} alt={service.title} />
                    <div className="service-icon">
                      <FontAwesomeIcon icon={service.icon} />
                    </div>
                  </div>
                  <div className="service-content">
                    <h2>{service.title}</h2>
                    <p>{service.description}</p>
                    <Link to={`/services/${service.id}`} className="read-more-btn">Read More</Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Make an Appointment */}
      <section className="section appointment-section" ref={appointmentRef} id="appointment">
        <div className="container">
          <div className="appointment-card animate-on-scroll">
            <div className="appointment-icon animate-on-scroll animate-delay-1">
              <FontAwesomeIcon icon={faUserMd} />
            </div>
            <div className="appointment-content animate-on-scroll animate-delay-2">
              <h2>Make an Appointment</h2>
              <p>Ready to schedule your visit? Contact us today to book an appointment with our experienced medical team.</p>
              <Link to="https://www.hotdoc.com.au/medical-centres/tea-gardens-NSW-2324/tea-gardens-gp/doctors" className="appointment-btn animate-on-scroll animate-delay-3">Book an Appointment</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
